import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchContent from './SearchContent';
import RefineSearch from './RefineSearch';
import FooterContent from '../../components/core/Footer/FooterContent';
import NoResults from './NoResults';
import CircularLoader from '../../components/core/Loader/CircularLoader';
import { Box, makeStyles } from '@material-ui/core';
import Filter from './FilterModal';
import { actionGetHomePageRequest } from '../../store/HomePage/HomePageAction';
import {
  actionClearFailedVenues,
  actionGetSpecificVenueAvailabilityRequest,
} from '../../store/Availability/AvailabilityAction';
import moment from 'moment';
import '../../assets/style/search.css';
import {
  actionCacheCuratedPickFilter,
  actionCacheCurrentFilters,
  actionResetCachedFilters,
  actionResetCurrentCuratedPickFilter,
  actionResetCurrentFilters,
  actionSetCurrentCuratedPickFilter,
  actionSetFilterOverlayVisible,
} from '../../store/Filter/FilterAction';
import {
  actionCacheSearchSessionId,
  actionResetSearchSessionIdCache,
  actionSetSearchCachedQuery,
  actionSetSearchCurrentInputValue,
  actionSetSearchCurrentQuery,
  actionSetSearchCurrentSessionId,
} from '../../store/Search/SearchAction';
import { getRandomString } from '../../utils/useGeneratedKey';
import {
  isValidSearchRedirect,
  mapToQueryParam,
  useScrollContextRef,
  useSearchRedirect,
} from '../../components/search/helpers';
import {
  actionFindVenuesRequest,
  actionResetVenueList,
} from '../../store/Venues/VenuesAction';
import {
  BASE_SEARCH_PATHNAME,
  DEFAULT_REDIRECT,
} from '../../components/search/constants';
import { useHistory, useLocation } from 'react-router';
import { useComponentWillUnmount } from '../../utils/useComponentWillUnmount';
import {
  trackSiteSearch,
  trackClickWithDescription,
} from '../../utils/useOneTag';
import {
  actionCacheRefinedSearchValues,
  actionListAvailabilityVenuesClear,
} from '../../store/Availability/AvailabilityAction';
import { DateTime } from 'luxon';
import SearchFilters from './SearchFilters';
import { actionSetShouldShowErrorInPage } from '../../store/Error/ErrorAction.js';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    width: '100vw',
    height: '100%',
  },
  loaderContainer: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  scroll: {
    display: 'grid',
    height: '100%',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  },
  filterControls: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(3, 0),
    height: 32,
  },
  filterButtonContainer: {
    margin: theme.spacing(0, 0.5),
  },
}));

const resolveCurrentSelectedVenues = (venues, activeCuratedPick, dispatch) => {
  const { venuesByCuratedPick, list: venueList } = venues;
  let currentSelectedVenues;
  if (activeCuratedPick) {
    currentSelectedVenues = venuesByCuratedPick
      .filter((venueGroup) => venueGroup.curatedPickType === activeCuratedPick)
      .map((venueGroup) => venueGroup.venues)
      .flat();
  } else {
    currentSelectedVenues = venueList.filter(
      (venue) => venue.venueType === 'REGULAR'
    );

    if (currentSelectedVenues.length === 0 && venueList.length > 0) {
      dispatch(
        actionSetCurrentCuratedPickFilter({
          curatedPickType: 'event',
        })
      );
    }
  }
  return currentSelectedVenues;
};

const Search = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { markets, venues } = useSelector((state) => state);
  const { inLocalMarket, venuesByCuratedPick } = venues;
  const { market } = markets;
  const {
    cachedFilters,
    currentFilters,
    currentCuratedPickFilter,
    filterOverlayVisible,
  } = useSelector((state) => state.filters);
  const { cachedQuery, cachedSessionId, currentQuery, currentSessionId } =
    useSelector((state) => state.search);
  const { list: venueList } = useSelector((state) => state.venues);
  const [isRefineSearchOpen, setIsRefineSearchOpen] = useState(false);
  const { refinedDate, refinedTime, refinedGuests } = useSelector(
    (state) => state.availability
  );
  const [isRefineSearchActive, setIsRefineSearchActive] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { failedVenueIds } = useSelector((state) => state.availability);

  const openFilterModalContainerHandler = () => {
    trackClickWithDescription('search-revamp-filter-button', 'Button', '');
    dispatch(actionSetFilterOverlayVisible(true));
  };

  const openRefineSearchHandler = () => {
    trackClickWithDescription(
      'search-revamp-refine-search-button',
      'Button',
      ''
    );
    setIsRefineSearchOpen(true);
  };

  const availableCuratedPicks = venuesByCuratedPick.map((venueGroup) => {
    return venueGroup.curatedPickType;
  });
  const activeCuratedPick = currentCuratedPickFilter?.curatedPickType;
  const currentSelectedVenues = useMemo(
    () => resolveCurrentSelectedVenues(venues, activeCuratedPick, dispatch),
    [venues, activeCuratedPick, dispatch]
  );
  const scrollRef = useScrollContextRef();
  const searchRedirect = useSearchRedirect();
  const setSearchSession = useCallback(
    (sessionId) => {
      if (sessionId === currentSessionId) return;

      dispatch(actionSetSearchCurrentSessionId(sessionId));
      const pathname = BASE_SEARCH_PATHNAME;
      const search = mapToQueryParam({ sessionId });
      const redirectTo = isValidSearchRedirect(location.state?.redirectTo)
        ? location.state.redirectTo
        : DEFAULT_REDIRECT;

      if (!location.search) {
        history.replace(
          { pathname, search },
          { ...location.state, redirectTo }
        );
      } else {
        history.push({ pathname, search }, { ...location.state, redirectTo });
      }
    },
    [location, history, currentSessionId, dispatch]
  );

  useComponentWillUnmount(() => {
    dispatch(actionCacheSearchSessionId());
    dispatch(actionCacheCurrentFilters());
    dispatch(actionSetSearchCurrentQuery(''));
    dispatch(actionCacheCuratedPickFilter());
    dispatch(actionCacheRefinedSearchValues());
    dispatch(actionResetVenueList());
    dispatch(actionListAvailabilityVenuesClear());
  });

  const resetSearchQuery = useCallback(() => {
    dispatch(actionSetSearchCurrentInputValue(''));
    dispatch(actionSetSearchCurrentQuery(''));
  }, [dispatch]);

  const search = useCallback(
    ({
      filters = currentFilters,
      keyword = currentQuery,
      marketId = market.id,
      sessionId = getRandomString(),
      date = DateTime.fromJSDate(refinedDate).toISODate() ?? undefined,
      partySize = refinedGuests?.split(' ')?.[0] ?? undefined,
      startTime = '6:00',
      endTime = '5:45',
    } = {}) => {
      setIsLoading(true);
      setSearchSession(sessionId);
      dispatch(
        actionFindVenuesRequest({
          filters,
          keyword,
          marketId,
          date,
          partySize,
          startTime,
          endTime,
        })
      );

      if (cachedQuery !== keyword) {
        dispatch(actionSetSearchCachedQuery(keyword));
      }
    },
    [
      setIsLoading,
      setSearchSession,
      dispatch,
      cachedQuery,
      currentFilters,
      currentQuery,
      market.id,
      refinedDate,
      refinedGuests,
    ]
  );

  useEffect(() => {
    // site search tracker
    if (currentQuery) {
      trackSiteSearch(
        currentQuery,
        `Search revamp - Search page - Market ID: ${market.id}`,
        JSON.stringify(currentFilters)
      );
    }
  }, [currentQuery, market.id, currentFilters]);

  const resetFilters = useCallback(
    () => dispatch(actionResetCurrentFilters()),
    [dispatch]
  );

  const resetAllSearch = useCallback(() => {
    resetSearchQuery();
    resetFilters();
    dispatch(actionResetCurrentCuratedPickFilter());
    history.replace({ BASE_SEARCH_PATHNAME }, { searchRedirect });
  }, [dispatch, history, searchRedirect, resetSearchQuery, resetFilters]);

  //re-filter when currentQuery changes, specifically for when we clear current query
  useEffect(() => {
    if (!isLoading) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuery]);

  // Re-filter when currentFilters updates, but not when already finding venues
  useEffect(() => {
    if (isMounted && venues.findVenuesStatus?.loading === false) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters]);

  // Set loading to false after finding venues
  useEffect(() => {
    if (venues.findVenuesStatus?.loading === false) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues.findVenuesStatus]);

  // Reset stale filter cache after mounting
  useEffect(() => {
    if (cachedFilters != null && isMounted) {
      dispatch(actionResetCachedFilters());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  // Reset stale search session ID after mounting
  useEffect(() => {
    if (cachedSessionId && isMounted) {
      dispatch(actionResetSearchSessionIdCache());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  // Search when market changes
  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, refinedDate, refinedGuests]);

  // if the component is not mounted and currentQuery has a value, run search and mount
  useEffect(() => {
    if (!isMounted) {
      // Don't search if list already exists (when page is refreshed)
      if (
        venues.findVenuesStatus?.loading == null &&
        venues.searchVenuesStatus?.success == null &&
        venues.venuesByMarketStatus?.success == null
      ) {
        const sessionId = currentSessionId
          ? currentSessionId
          : getRandomString();
        search({ sessionId });
      }

      setIsMounted(true);
    }
  }, [isMounted, search, currentSessionId, venues]);

  const handleCuratedPickSelect = (existingCuratedPick, newCuratedPick) => {
    if (newCuratedPick) {
      dispatch(
        actionSetCurrentCuratedPickFilter({
          curatedPickType: newCuratedPick,
        })
      );
    } else {
      dispatch(actionResetCurrentCuratedPickFilter());
    }
  };

  useEffect(() => {
    setIsRefineSearchActive(refinedDate && refinedGuests && refinedTime);
  }, [refinedDate, refinedGuests, refinedTime]);

  useEffect(() => {
    dispatch(
      actionGetHomePageRequest({ marketId: market.id, forceCall: true })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market.id]);

  useEffect(() => {
    dispatch(actionSetShouldShowErrorInPage(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(actionClearFailedVenues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueList]);

  //reload availability when the venueList changes due to market or search change
  useEffect(() => {
    handleOnUpdateAvailabilityParameters({
      selectedDate: refinedDate,
      selectedTime: refinedTime,
      selectedGuests: refinedGuests,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueList]);

  const handleOnUpdateAvailabilityParameters = (updates) => {
    dispatch(actionListAvailabilityVenuesClear());

    const { selectedDate, selectedTime, selectedGuests } = updates;

    if (
      selectedDate === null &&
      selectedTime === null &&
      selectedGuests === null
    ) {
      return;
    }

    const date = moment(selectedDate).format('YYYY-MM-DD');
    const partySize = parseInt(selectedGuests.split(' ')?.[0]);

    dispatch(
      actionGetSpecificVenueAvailabilityRequest({
        venues: venueList,
        date,
        partySize,
      })
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.scroll} ref={scrollRef}>
        {(isLoading || (currentSelectedVenues.length > 0 && inLocalMarket)) && (
          <SearchFilters
            isRefineSearchActive={isRefineSearchActive}
            openFilterModalContainerHandler={openFilterModalContainerHandler}
            openRefineSearchHandler={openRefineSearchHandler}
            resetSearchQuery={resetSearchQuery}
          />
        )}
        {isLoading ? (
          <div className={classes.loaderContainer}>
            <CircularLoader />
          </div>
        ) : !currentSelectedVenues || inLocalMarket === false ? (
          <NoResults clearSearch={resetAllSearch} />
        ) : (
          <SearchContent
            venues={currentSelectedVenues}
            availableCuratedPicks={availableCuratedPicks}
            activeCuratedPick={activeCuratedPick}
            resetAllSearch={resetAllSearch}
            handleCuratedPickSelect={handleCuratedPickSelect}
            failedVenueIds={failedVenueIds}
          />
        )}
        {filterOverlayVisible && <Filter />}
        <RefineSearch
          open={isRefineSearchOpen}
          onClose={() => setIsRefineSearchOpen(false)}
          onUpdate={handleOnUpdateAvailabilityParameters}
        />
        <FooterContent isSearch isLoading={isLoading} />
      </Box>
    </Box>
  );
};
export default Search;
